import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaBullhorn, FaCalendarAlt, FaSearchLocation, FaUsers } from "react-icons/fa";
import PortalFormPage from "./PortalFormPage";
import PortalFormPanel from "./PortalFormPanel";
import PortalAlert from "./PortalAlert";
import { Options, UserOptions } from "../pages/dm/components/common";
import { useProjectForm } from "../pages/dm/components/ProjectForm";
import { errorMessage } from "../pages/dm/dmApi";

function TenantDmProjectNew({ api, basePath = "/portal/digital-marketing" }) {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState("");
  const { form, set, onLinkTenantProject, tenantProjects, saving, error, setError, submit } = useProjectForm({
    api,
    onSaved: (saved) => navigate(`${basePath}/${saved.Dm_Project_Id}`),
  });

  useEffect(() => {
    api.meta().then(setMeta).catch((err) => setMetaError(errorMessage(err, "Could not load options")));
  }, [api]);

  return (
    <PortalFormPage
      title="New Project"
      backLabel="Back to Digital Marketing"
      onBack={() => navigate(basePath)}
      onSubmit={submit}
      submitting={saving}
      submitLabel="Create project"
      submittingLabel="Creating…"
      footerNote={
        <>
          Fields marked <span className="cportal-required">*</span> are required
        </>
      }
      className="cportal-lead-form cportal-lead-form--panels cportal-form-page--two-col"
    >
      <PortalAlert type="error" message={error || metaError} onClear={() => { setError(""); setMetaError(""); }} />

      <PortalFormPanel icon={<FaBullhorn />} title="Project" hint="What you are promoting">
        <div className="cportal-form-grid">
          {tenantProjects.length > 0 ? (
            <div className="cportal-field cportal-field--full">
              <label>Link to your project / property</label>
              <select value={form.Tenant_Project_Id} onChange={onLinkTenantProject}>
                <option value="">Not linked</option>
                {tenantProjects.map((p) => (
                  <option key={p.Tenant_Project_Id} value={p.Tenant_Project_Id}>
                    {p.Project_Name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="cportal-field">
            <label>
              Project name <span className="cportal-required">*</span>
            </label>
            <input required value={form.Project_Name} onChange={set("Project_Name")} placeholder="e.g. Eastern Pride" />
          </div>
          <div className="cportal-field">
            <label>Short code</label>
            <input value={form.Short_Code} onChange={set("Short_Code")} maxLength={20} />
          </div>
          <div className="cportal-field">
            <label>Priority</label>
            <select value={form.Priority} onChange={set("Priority")}>
              <Options values={meta?.priorities} />
            </select>
          </div>
          <div className="cportal-field">
            <label>Stage</label>
            <select value={form.Stage} onChange={set("Stage")}>
              <Options values={meta?.stages} />
            </select>
          </div>
          <div className="cportal-field cportal-field--full">
            <label>Property / business location</label>
            <input value={form.Location} onChange={set("Location")} placeholder="Dhandumailaram, Hyderabad" />
          </div>
        </div>
      </PortalFormPanel>

      <PortalFormPanel icon={<FaCalendarAlt />} title="Schedule" hint="When the campaign runs">
        <div className="cportal-form-grid">
          <div className="cportal-field">
            <label>Start date</label>
            <input type="date" value={form.Start_Date} onChange={set("Start_Date")} />
          </div>
          <div className="cportal-field">
            <label>End date</label>
            <input type="date" value={form.End_Date} onChange={set("End_Date")} />
          </div>
        </div>
      </PortalFormPanel>

      <PortalFormPanel icon={<FaUsers />} title="Team" hint="Who works on and reviews this project">
        <div className="cportal-form-grid cportal-form-grid--3">
          <div className="cportal-field">
            <label>Assigned to</label>
            <select value={form.Assigned_To} onChange={set("Assigned_To")}>
              <UserOptions users={meta?.users} />
            </select>
          </div>
          <div className="cportal-field">
            <label>Prepared by</label>
            <select value={form.Prepared_By} onChange={set("Prepared_By")}>
              <UserOptions users={meta?.users} blank="—" />
            </select>
          </div>
          <div className="cportal-field">
            <label>Reviewed by</label>
            <select value={form.Reviewed_By} onChange={set("Reviewed_By")}>
              <UserOptions users={meta?.users} blank="—" />
            </select>
          </div>
        </div>
      </PortalFormPanel>

      <PortalFormPanel icon={<FaSearchLocation />} title="Targeting" hint="Keywords, areas and the brief">
        <div className="cportal-form-grid">
          <div className="cportal-field cportal-field--full">
            <label>Keywords (comma separated)</label>
            <input value={form.Keywords} onChange={set("Keywords")} placeholder="villas in hyderabad, gated community" />
          </div>
          <div className="cportal-field cportal-field--full">
            <label>Target locations (comma separated, optional radius e.g. LB Nagar (R2km))</label>
            <input
              value={form.Target_Locations}
              onChange={set("Target_Locations")}
              placeholder="LB Nagar (R2km), BN Reddy (R3km), Hayatnagar"
            />
          </div>
          <div className="cportal-field cportal-field--full">
            <label>Description / brief</label>
            <textarea rows={3} value={form.Description} onChange={set("Description")} />
          </div>
        </div>
      </PortalFormPanel>
    </PortalFormPage>
  );
}

export default TenantDmProjectNew;
